import { useEffect, useState, useCallback } from "react"

import { ContentObjectItem } from "@vertesia/common"
import { X, FileCheck } from "lucide-react"
import { toast } from "sonner"

import { ProjectFiltersPanel } from "@/components/filters/ProjectFiltersPanel"
import { Button } from "@ui/button"
import { transformProjectPropertiesFacetsWithFilter } from "@/lib/facets"
import { removeEmptyValues } from "@/lib/filters"
import { FilterLogic, FilterProperty, PropertyFacet, SearchFilters } from "@/models/facets"
import { useWatchDocumentSearchFacets, useWatchDocumentSearchResult } from "@/plugins/providers/document/DocumentSearchContext"
import { ProjectDataTable } from "@/components/datatables/ProjectDataTable"

import { DynamicColumn } from "@/models/datatable"
import { ReportSearchField } from "@/models/report"

interface ProjectSelectionSearchPanelProps {
    children?: React.ReactNode
    types: string[]
    columns: DynamicColumn[]
    searches: ReportSearchField[]
    filters: FilterProperty[]
    filters_logic: FilterLogic[]
    onMultipleSelection: (documents: ContentObjectItem[]) => void
}
export function ProjectSelectionSearchPanel({ children, types, columns, searches, filters, filters_logic, onMultipleSelection }: Readonly<ProjectSelectionSearchPanelProps>) {
    const { search, isSearching, error, objects, hasMore } = useWatchDocumentSearchResult()
    const facets = useWatchDocumentSearchFacets()

    const [searchFilters, setSearchFilters] = useState<SearchFilters>({})
    const [properties, setProperties] = useState<PropertyFacet[]>([])
    const [hasSearched, setHasSearched] = useState(false)

    const [isLoadingMore, setIsLoadingMore] = useState(false)

    const [selectionDocuments, setSelectionDocuments] = useState<ContentObjectItem[]>([])
    const [filteredObjects, setFilteredObjects] = useState<ContentObjectItem[]>([])
    const [noFilterMatch, setNoFilterMatch] = useState(false)

    const searchObjects = useCallback(async () => {
        const properties = removeEmptyValues(searchFilters?.properties)
        const matches = removeEmptyValues(searchFilters?.matches)

        if (Object.keys(matches).length === 0) {
            search.reset(false)
            setHasSearched(false)
            return
        }

        // Transform properties to use $all for array values (AND logic)
        const transformedProperties = Object.fromEntries(
            Object.entries(properties).map(([key, value]) => {
                if (Array.isArray(value)) {
                    return [key, value.length === 1 ? value[0] : { $all: value }]
                }
                return [key, value]
            })
        )

        const regexMatches = Object.fromEntries(
            Object.entries(matches).map(([property, value]) => {
                const filter = filters.find(f => f.field === property)
                const isStringFilter = filter?.match && filter?.type === 'string'

                // Handle arrays of values (AND logic - match ALL values)
                if (Array.isArray(value)) {
                    if (isStringFilter) {
                        // For regex matches with multiple values, use $all with regex
                        return [
                            property,
                            value.length === 1
                                ? { $regex: value[0], $options: 'i' }
                                : { $all: value.map(v => new RegExp(v, 'i')) }
                        ]
                    }
                    return [property, { $all: value }]
                }

                // Handle single string value (backward compatibility)
                const shouldUseRegex = isStringFilter && typeof value === 'string'
                return [
                    property,
                    shouldUseRegex ? { $regex: value, $options: 'i' } : value
                ]
            })
        )

        const query: any = {
            types,
            ...transformedProperties,
            match: { ...regexMatches }
        }

        search.query = query
        setHasSearched(true)
        await search.search()
    }, [searchFilters, search, types, filters])

    const loadMoreDocuments = useCallback(async () => {
        if (isLoadingMore || !hasMore) {
            return
        }

        setIsLoadingMore(true)

        try {
            await search.loadMore()
        } catch (error) {
            console.error("Error loading more documents:", error)
        } finally {
            setIsLoadingMore(false)
        }
    }, [isLoadingMore, hasMore, search])

    const handleMultipleSelection = useCallback((documents: ContentObjectItem[]) => {
        setSelectionDocuments(documents)
    }, [])

    const confirmMultipleSelection = useCallback(() => {
        onMultipleSelection(selectionDocuments)
    }, [onMultipleSelection, selectionDocuments])

    const applyFiltersLogic = useCallback((objects: ContentObjectItem[]) => {
        // If no objects, don't apply filtering logic
        if (objects.length === 0) {
            setFilteredObjects([])
            setNoFilterMatch(false)
            return
        }

        // If no filters_logic, return all objects
        if (!filters_logic || filters_logic.length === 0) {
            setFilteredObjects(objects)
            setNoFilterMatch(false)
            return
        }

        // Sort filters by priority (lowest first)
        const sortedFilters = [...filters_logic].sort((a, b) => a.priority - b.priority)

        // Try each filter in priority order
        for (const filterLogic of sortedFilters) {
            const matchingObjects = objects.filter((obj) => {
                if (filterLogic.type === "type") {
                    // Check object.type.id === filter_logic.value
                    return (obj as any).type?.id === filterLogic.value
                } else {
                    // Check object.properties[filter_logic.value] === filter_logic.additional
                    return (obj as any).properties?.[filterLogic.value] === filterLogic.additional
                }
            })

            // If we found matches, return them and stop
            if (matchingObjects.length > 0) {
                setFilteredObjects(matchingObjects)
                setNoFilterMatch(false)
                return
            }
        }

        // No matches found for any filter (but we had objects to filter)
        setFilteredObjects([])
        setNoFilterMatch(true)
    }, [filters_logic])

    const initialSearch = async () => {
        search.query = {}
        await search.searchFacets()
    }

    useEffect(() => {
        applyFiltersLogic(objects)
    }, [objects, applyFiltersLogic])

    useEffect(() => {
        if (!error) {
            return
        }

        toast.error("Error while searching documents", {
            description: error.message,
            duration: 5000
        })
    }, [error])

    useEffect(() => {
        setProperties(transformProjectPropertiesFacetsWithFilter(facets, searches))
    }, [facets, searches])

    useEffect(() => {
        const properties = removeEmptyValues(searchFilters?.properties)
        const matches = removeEmptyValues(searchFilters?.matches)

        if (Object.keys(matches).length === 0 && hasSearched) {
            search.reset(false)
            setHasSearched(false)
        } else if (Object.keys(matches).length > 0) {
            searchObjects()
        } else if (Object.keys(matches).length === 0 && Object.keys(properties).length === 0) {
            initialSearch()
        }
    }, [searchFilters, hasSearched, search, searchObjects])

    useEffect(() => {
        initialSearch()
    }, [])

    return (
        <div className="flex h-full w-full">
            <ProjectFiltersPanel
                properties={properties}
                filters={filters}
                onFiltersChange={setSearchFilters}
            />
            <div className='w-full h-full flex flex-col gap-2'>
                <div className='w-full flex flex-row gap-2 items-center justify-end p-2 pb-0'>
                    <div className='flex h-10 gap-2 items-center'>
                        {
                            (selectionDocuments.length !== 0) && (
                                <>
                                    <span className='text-sm text-gray-500'>{selectionDocuments.length} project{selectionDocuments.length > 1 ? "s" : ""} selected</span>
                                    <Button variant='ghost' className='text-gray-500' onClick={() => {
                                        setSelectionDocuments([])
                                    }}>
                                        <X />
                                    </Button>
                                    <Button onClick={() => {
                                        confirmMultipleSelection()
                                    }}>
                                        <FileCheck />
                                        Confirm Selection
                                    </Button>
                                </>
                            )
                        }
                        {children}
                    </div>
                </div>
                <div className="flex-grow w-full overflow-auto px-2">
                    <ProjectDataTable
                        columns={columns}
                        data={filteredObjects}
                        loading={isSearching}
                        loadingMore={isLoadingMore}
                        hasMore={hasMore}
                        loadMore={loadMoreDocuments}
                        onMultipleSelection={handleMultipleSelection}
                        selection={selectionDocuments}
                        hasSearched={hasSearched}
                        noFilterMatch={noFilterMatch}
                    />
                </div>
            </div>
        </div>
    )
}